import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, CircularProgress } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { get } from 'utils/lodash'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'

import { useLazyGetEmailNotificationDetailsQuery } from 'api/email-notification/emailNotification'

import T from 'T'

import EmailActivityContent from './email-activity/EmailActivityContent'
import EmailDetailsContent from './email-activity/EmailDetailsContent'

const EmailActivity = ({ chipGridWidth = 120, email }) => {
  const event = get(email, 'event', '')
  const emailTo = get(email, 'email', '')
  const timeStamp = get(email, 'timestamp')
  const emailNotificationId = get(email, 'emailNotificationId')
  const utcDateTime = timeStamp ? new Date(timeStamp * 1000).toISOString() : ''
  const { fullDateTime } = convertDateTimeToPreferedTimezone({ dateTime: utcDateTime })

  const [expanded, setExpanded] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [getEmailDetails, { data: emailDetailsData, isLoading, isError }] = useLazyGetEmailNotificationDetailsQuery()

  const getSendType = () => {
    if (emailNotificationId) {
      return T.CUSTOM
    }
    return T.EMAIL
  }

  const sendType = getSendType()

  const handleAccordionChange = (_, isExpanded) => {
    setExpanded(isExpanded)
    if (isExpanded && !hasFetched && emailNotificationId) {
      getEmailDetails({ emailNotificationId })
      setHasFetched(true)
    }
  }

  const subject = get(emailDetailsData, 'subject', '')
  const body = get(emailDetailsData, 'body', '')
  const toRecipientEmails = get(emailDetailsData, 'toRecipientEmails', [])
  const ccRecipientEmails = get(emailDetailsData, 'ccRecipientEmails', [])
  const defaultAttachments = get(emailDetailsData, 'attachments.defaultAttachments', [])
  const customAttachments = get(emailDetailsData, 'attachments.customAttachments', [])

  const contentProps = { chipGridWidth, event, emailTo, fullDateTime, sendType }

  // If no emailNotificationId, render without accordion
  if (!emailNotificationId) {
    return (
      <Box sx={{ ml: 4, width: '100%' }}>
        <EmailActivityContent {...contentProps} />
      </Box>
    )
  }

  // Render as accordion when emailNotificationId exists
  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      disableGutters
      elevation={0}
      sx={{
        width: '100%',
        '&:before': { display: 'none' },
        backgroundColor: 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          padding: 0,
          minHeight: 'auto',
          flexDirection: 'row-reverse',
          '& .MuiAccordionSummary-content': {
            margin: 0,
            marginLeft: 1,
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(180deg)',
          },
        }}
      >
        <EmailActivityContent {...contentProps} />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2, pb: 1, pl: 2 }}>
        {isLoading && (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {T.LOADING_EMAIL_DETAILS}
            </Typography>
          </Box>
        )}

        {isError && (
          <Typography variant="body2" color="error">
            {T.FAILED_TO_LOAD_EMAIL_DETAILS}
          </Typography>
        )}

        {!isLoading && !isError && hasFetched && (
          <EmailDetailsContent
            subject={subject}
            body={body}
            toRecipientEmails={toRecipientEmails}
            ccRecipientEmails={ccRecipientEmails}
            defaultAttachments={defaultAttachments}
            customAttachments={customAttachments}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

EmailActivity.propTypes = {
  chipGridWidth: PropTypes.number,
  email: PropTypes.object.isRequired,
}

export default EmailActivity
